import type { ComputedRef, Ref, ShallowRef } from 'vue';

import type OLMap from 'ol/Map';

import type { GeoFencingRaw } from '@/api/ApiTypes';

import type { MapFeature } from '@/components/map/features/MapFeatures';
import type { FeatureData, MapMode, ActiveSubNetworkWithColor } from '@/components/map/types/mapFeatureTypes';
import type { CcmMapData } from '@/components/map/cnetConstructorMode/types/ccmTypes';
import type { MapFeatureData, MapDisplayOptions } from '@/components/map/useMapData';
import type {
	BsFilterSelected,
	MapSourceFeatureGroupContext,
} from '@/components/map/features/useMapSourceFeatureGroups';


type MapModeStore = {
	mode: MapMode;
	geoFencing: GeoFencingRaw | null;
};


type MapUserStore = {
	allowToSeeMountpointNames: boolean;
};


type UseMapSourceContextParams = {
	featureData: MapFeatureData;
	displayOptions: MapDisplayOptions;
	bsFilterSelectedRef: Ref<BsFilterSelected>;
	focusedMountpoint: Ref<string>;
	hoveredFeatureData: ComputedRef<FeatureData | null>;
	activeSubNetworks: ComputedRef<ActiveSubNetworkWithColor[]>;
	baseStationsStore: MapModeStore;
	userStore: MapUserStore;
	mapDataCCM: CcmMapData;
	baseStationFeatures: ShallowRef<MapFeature[]>;
	roversHistoryPathsFeatures: ShallowRef<MapFeature[]>;
};


type BuildSourceContext = (doFit: boolean, mapInstance: OLMap) => MapSourceFeatureGroupContext;


// Собирает контекст для collectMapSourceFeatures из реактивных источников,
// читая .value лениво в момент вызова (внутри debounced updateSource).
export function useMapSourceContext(params: UseMapSourceContextParams): BuildSourceContext {
	const {
		featureData,
		displayOptions,
		bsFilterSelectedRef,
		focusedMountpoint,
		hoveredFeatureData,
		activeSubNetworks,
		baseStationsStore,
		userStore,
		mapDataCCM,
		baseStationFeatures,
		roversHistoryPathsFeatures,
	} = params;

	return (doFit, mapInstance) => ({
		mode: baseStationsStore.mode,
		doFit,
		mapInstance,
		gridpoints: featureData.gridpoints.value,
		baseStations: featureData.baseStations.value,
		roversActive: featureData.roversActive.value,
		roversFailed: featureData.roversFailed.value,
		profiles: featureData.profiles.value,
		subNetworksGeojson: featureData.subNetworksGeojson.value,
		baselines: featureData.baselines.value,
		currentRoversSelectedFeatures: featureData.currentRoversSelectedFeatures.value,
		roversHistoryPaths: featureData.roversHistoryPaths.value,
		roversHistoryAutoUpdate: displayOptions.roversHistoryAutoUpdate.value,
		bsFilterSelected: bsFilterSelectedRef.value,
		fillCoverageArea: displayOptions.fillCoverageArea.value,
		qualityColorMode: displayOptions.qualityColorMode.value,
		focusedMountpoint: focusedMountpoint.value,
		allSubnetworksMode: displayOptions.allSubnetworksMode.value,
		allowToSeeMountpointNames: userStore.allowToSeeMountpointNames,
		geoFencing: baseStationsStore.geoFencing,
		activeSubNetworks: activeSubNetworks.value,
		anchorPoint: mapDataCCM.anchorPoint.value,
		ccmGridpoints: [...mapDataCCM.gridpoints.value],
		ccmOtherGridpoints: [...mapDataCCM.otherGridpoints.value],
		hoveredFeatureData: hoveredFeatureData.value,
		baseStationFeatures,
		roversHistoryPathsFeatures,
	});
}


export type {
	BuildSourceContext,
	UseMapSourceContextParams,
};
